import { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Toast from 'blocks.simple/toast/toast'
import { secondFactorApi } from 'core/api/secondFactor'
import { IWebSocketError } from 'core/api/models'
import { AppDispatch } from 'core/store'
import { emitError } from 'features/appNotifications/AppNotifications.state'
import { applySessionTokens } from 'blocks.app/user/user.state'
import translate from 'core/translate'
import {
    BeginSecondFactorActionResponse,
    ConfirmSecondFactorActionResult,
    SecondFactorActionKind,
    SecondFactorFlowStep,
    SecondFactorMethod,
    SecondFactorStatus,
    isBackupCodeComplete,
    isOtpComplete,
    isProofCodeReady,
    needsProofCode,
    normalizeBackupCode,
} from 'core/models/SecondFactor'
import { IUserTokens } from 'core/models/Users'

const SECOND_FACTOR_RESEND_TOO_SOON = 129

const getResendTooSoonRetryAfterS = (err: unknown): number | null => {
    const wsError = err as IWebSocketError | undefined
    if (Number(wsError?.errorCode) !== SECOND_FACTOR_RESEND_TOO_SOON) {
        return null
    }
    const retryAfterS = Number((wsError?.data as { retryAfterS?: number } | undefined)?.retryAfterS)
    return Number.isFinite(retryAfterS) && retryAfterS > 0 ? retryAfterS : 0
}

const showBeginResendTooSoonError = (err: unknown): boolean => {
    const retryAfterS = getResendTooSoonRetryAfterS(err)
    if (retryAfterS === null) {
        return false
    }
    const message =
        retryAfterS > 0
            ? translate('secondFactorResendTooSoon').replace('{{seconds}}', String(retryAfterS))
            : translate('secondFactorResendCode')
    Toast.error(message, {
        position: 'bottom-right',
        autoClose: 5000,
    })
    return true
}

export type SecondFactorFlowIntent = 'unbind_totp'

export interface StartSecondFactorFlowParams {
    action: SecondFactorActionKind
    method?: SecondFactorMethod
    userId?: number
    intent?: SecondFactorFlowIntent
}

export interface UseSecondFactorFlowOptions {
    onStatus?: (status: SecondFactorStatus) => void
    onDone?: (status: SecondFactorStatus | null) => void
}

const toStatus = (data?: ConfirmSecondFactorActionResult | SecondFactorStatus | null): SecondFactorStatus | null => {
    if (!data || data.method === undefined) {
        return null
    }

    const status: SecondFactorStatus = {
        method: data.method,
        authenticatorBound: Boolean(data.authenticatorBound),
        recoveryCodesRemaining: data.recoveryCodesRemaining ?? null,
        authenticatorBoundAt: data.authenticatorBoundAt ?? null,
    }

    return status
}

export const useSecondFactorFlow = (options: UseSecondFactorFlowOptions = {}) => {
    const dispatch = useDispatch<AppDispatch>()
    const optionsRef = useRef(options)
    optionsRef.current = options
    const sessionRef = useRef(0)
    const busyRef = useRef(false)

    const [step, setStep] = useState<SecondFactorFlowStep>('idle')
    const [isBusy, setIsBusy] = useState(false)
    const [errorKey, setErrorKey] = useState('')
    const [begin, setBegin] = useState<BeginSecondFactorActionResponse | null>(null)
    const [setup, setSetup] = useState<ConfirmSecondFactorActionResult | null>(null)
    const [completionToken, setCompletionToken] = useState<string | null>(null)
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
    const [proofCode, setProofCode] = useState('')
    const [totpCode, setTotpCode] = useState('')
    const [savedAck, setSavedAck] = useState(false)
    const [pendingAction, setPendingAction] = useState<StartSecondFactorFlowParams | null>(null)
    const [pendingStatus, setPendingStatus] = useState<SecondFactorStatus | null>(null)

    const isCurrentSession = useCallback((session: number) => session === sessionRef.current, [])

    const setBusy = useCallback((value: boolean) => {
        busyRef.current = value
        setIsBusy(value)
    }, [])

    const resetLocal = useCallback(() => {
        sessionRef.current += 1
        setBusy(false)
        setStep('idle')
        setErrorKey('')
        setBegin(null)
        setSetup(null)
        setCompletionToken(null)
        setRecoveryCodes([])
        setProofCode('')
        setTotpCode('')
        setSavedAck(false)
        setPendingAction(null)
        setPendingStatus(null)
    }, [setBusy])

    const finishWithStatus = useCallback(
        (status: SecondFactorStatus | null) => {
            if (status) {
                optionsRef.current.onStatus?.(status)
            }
            optionsRef.current.onDone?.(status)
            resetLocal()
        },
        [resetLocal]
    )

    const completeDone = useCallback(() => {
        finishWithStatus(pendingStatus)
    }, [finishWithStatus, pendingStatus])

    const applyTokensIfPresent = useCallback(
        async (tokens?: IUserTokens | null) => {
            const raw = tokens as (IUserTokens & { access_token?: string; refresh_token?: string }) | null | undefined
            const accessToken = raw?.accessToken || raw?.access_token
            const refreshToken = raw?.refreshToken || raw?.refresh_token
            if (!accessToken) {
                return
            }
            await dispatch(
                applySessionTokens({
                    accessToken,
                    refreshToken: refreshToken || '',
                })
            )
        },
        [dispatch]
    )

    const handleConfirmResult = useCallback(
        async (params: StartSecondFactorFlowParams, confirmed: ConfirmSecondFactorActionResult, session: number) => {
            if (!isCurrentSession(session)) {
                return
            }
            await applyTokensIfPresent(confirmed.tokens)
            if (!isCurrentSession(session)) {
                return
            }

            if (params.action === 'select' && params.method === 'totp' && confirmed.setupToken) {
                setSetup(confirmed)
                setStep('totp_setup')
                return
            }

            if (params.action === 'regenerate' && confirmed.recoveryCodes?.length) {
                setRecoveryCodes(confirmed.recoveryCodes)
                const status = toStatus(confirmed)
                if (status) {
                    optionsRef.current.onStatus?.(status)
                }
                setStep('recovery_codes')
                return
            }

            if (params.intent === 'unbind_totp') {
                const status = toStatus(confirmed)
                if (status) {
                    optionsRef.current.onStatus?.(status)
                }
                setPendingStatus(status)
                setStep('done')
                return
            }

            finishWithStatus(toStatus(confirmed))
        },
        [applyTokensIfPresent, finishWithStatus, isCurrentSession]
    )

    const start = useCallback(
        async (params: StartSecondFactorFlowParams) => {
            if (busyRef.current) {
                return
            }

            const session = sessionRef.current + 1
            sessionRef.current = session
            setBusy(true)
            setErrorKey('')
            setPendingAction(params)
            setBegin(null)
            setProofCode('')
            setTotpCode('')
            setSavedAck(false)
            setRecoveryCodes([])
            setSetup(null)
            setCompletionToken(null)
            setPendingStatus(null)

            try {
                const response = await secondFactorApi.beginSecondFactorAction({
                    action: params.action,
                    method: params.method,
                    userId: params.userId,
                })
                if (!isCurrentSession(session)) {
                    return
                }
                setBegin(response)

                if (needsProofCode(response.proofMethod, response.codeLength)) {
                    setStep('proof')
                    return
                }

                try {
                    const confirmed = await secondFactorApi.confirmSecondFactorAction({
                        actionToken: response.actionToken,
                    })
                    if (!isCurrentSession(session)) {
                        return
                    }
                    await handleConfirmResult(params, confirmed, session)
                } catch {
                    if (!isCurrentSession(session)) {
                        return
                    }
                    setErrorKey('secondFactorConfirmError')
                    dispatch(emitError('secondFactorConfirmError'))
                    setStep('idle')
                    setPendingAction(null)
                    setBegin(null)
                }
            } catch (err) {
                if (!isCurrentSession(session)) {
                    return
                }
                setErrorKey('secondFactorBeginError')
                if (!showBeginResendTooSoonError(err)) {
                    dispatch(emitError('secondFactorBeginError'))
                }
                setStep('idle')
                setPendingAction(null)
                setBegin(null)
            } finally {
                if (isCurrentSession(session)) {
                    setBusy(false)
                }
            }
        },
        [dispatch, handleConfirmResult, isCurrentSession, setBusy]
    )

    const resendProof = useCallback(async () => {
        if (!pendingAction || busyRef.current || step !== 'proof') {
            return
        }

        const session = sessionRef.current + 1
        sessionRef.current = session
        setBusy(true)
        setErrorKey('')
        setProofCode('')

        try {
            const response = await secondFactorApi.beginSecondFactorAction({
                action: pendingAction.action,
                method: pendingAction.method,
                userId: pendingAction.userId,
            })
            if (!isCurrentSession(session)) {
                return
            }
            setBegin(response)
            if (needsProofCode(response.proofMethod, response.codeLength)) {
                return
            }
            const confirmed = await secondFactorApi.confirmSecondFactorAction({
                actionToken: response.actionToken,
            })
            if (!isCurrentSession(session)) {
                return
            }
            await handleConfirmResult(pendingAction, confirmed, session)
        } catch (err) {
            if (!isCurrentSession(session)) {
                return
            }
            const retryAfterS = getResendTooSoonRetryAfterS(err)
            if (retryAfterS !== null) {
                showBeginResendTooSoonError(err)
                if (retryAfterS > 0) {
                    setBegin((prev) => (prev ? { ...prev, resendAfterS: retryAfterS } : prev))
                }
                return
            }
            setErrorKey('secondFactorBeginError')
            dispatch(emitError('secondFactorBeginError'))
        } finally {
            if (isCurrentSession(session)) {
                setBusy(false)
            }
        }
    }, [pendingAction, step, dispatch, handleConfirmResult, isCurrentSession, setBusy])

    const changeProofCode = useCallback((value: string) => {
        setProofCode(value)
        setErrorKey('')
    }, [])

    const submitProof = useCallback(
        async (codeOverride?: string) => {
            if (!begin || !pendingAction || busyRef.current) {
                return
            }
            const rawCode = codeOverride ?? proofCode
            const ready =
                isProofCodeReady(begin.proofMethod, rawCode, begin.codeLength) ||
                (begin.proofMethod === 'totp' && isBackupCodeComplete(rawCode))
            if (!ready) {
                return
            }
            if (codeOverride) {
                setProofCode(codeOverride)
            }

            const normalizedBackup = normalizeBackupCode(rawCode)
            const codeToSend =
                begin.proofMethod === 'totp' &&
                !isOtpComplete(rawCode, begin.codeLength || 6) &&
                normalizedBackup.length >= 8
                    ? `${normalizedBackup.slice(0, 4)}-${normalizedBackup.slice(4, 8)}`
                    : rawCode

            const session = sessionRef.current
            setBusy(true)
            setErrorKey('')
            try {
                const confirmed = await secondFactorApi.confirmSecondFactorAction({
                    actionToken: begin.actionToken,
                    code: codeToSend,
                })
                if (!isCurrentSession(session)) {
                    return
                }
                await handleConfirmResult(pendingAction, confirmed, session)
            } catch {
                if (!isCurrentSession(session)) {
                    return
                }
                setErrorKey('secondFactorConfirmError')
            } finally {
                if (isCurrentSession(session)) {
                    setBusy(false)
                }
            }
        },
        [begin, pendingAction, proofCode, handleConfirmResult, isCurrentSession, setBusy]
    )

    const submitTotpSetup = useCallback(
        async (codeOverride?: string) => {
            const codeToSend = codeOverride ?? totpCode
            if (!setup?.setupToken || !isOtpComplete(codeToSend) || busyRef.current) {
                return
            }
            if (codeOverride) {
                setTotpCode(codeOverride)
            }

            const session = sessionRef.current
            setBusy(true)
            setErrorKey('')
            try {
                const bound = await secondFactorApi.confirmAuthenticatorBinding({
                    setupToken: setup.setupToken,
                    code: codeToSend,
                })
                if (!isCurrentSession(session)) {
                    return
                }
                setCompletionToken(bound.completionToken)
                setRecoveryCodes(bound.recoveryCodes || [])
                setSavedAck(false)
                setStep('recovery_codes')
            } catch {
                if (!isCurrentSession(session)) {
                    return
                }
                setErrorKey('totpEnrollmentConfirmError')
                dispatch(emitError('totpEnrollmentConfirmError'))
            } finally {
                if (isCurrentSession(session)) {
                    setBusy(false)
                }
            }
        },
        [setup, totpCode, dispatch, isCurrentSession, setBusy]
    )

    const finalizeRecoveryCodes = useCallback(async () => {
        if (!savedAck || busyRef.current) {
            return
        }

        if (!completionToken) {
            setStep('done')
            return
        }

        const session = sessionRef.current
        setBusy(true)
        setErrorKey('')
        try {
            const finalized = await secondFactorApi.finalizeAuthenticatorBinding({
                completionToken,
                saved: true,
            })
            if (!isCurrentSession(session)) {
                return
            }
            await applyTokensIfPresent(finalized && finalized.tokens)
            if (!isCurrentSession(session)) {
                return
            }
            const status = toStatus(finalized)
            const showDoneScreen =
                pendingAction?.action === 'regenerate' ||
                (pendingAction?.action === 'select' && pendingAction.method === 'totp')
            if (showDoneScreen) {
                if (status) {
                    optionsRef.current.onStatus?.(status)
                }
                setPendingStatus(status)
                setStep('done')
                return
            }
            finishWithStatus(status)
        } catch {
            if (!isCurrentSession(session)) {
                return
            }
            setErrorKey('secondFactorFinalizeError')
            dispatch(emitError('secondFactorFinalizeError'))
        } finally {
            if (isCurrentSession(session)) {
                setBusy(false)
            }
        }
    }, [
        savedAck,
        completionToken,
        dispatch,
        applyTokensIfPresent,
        finishWithStatus,
        pendingAction,
        isCurrentSession,
        setBusy,
    ])

    return {
        step,
        isBusy,
        errorKey,
        begin,
        setup,
        recoveryCodes,
        proofCode,
        setProofCode: changeProofCode,
        totpCode,
        setTotpCode,
        savedAck,
        setSavedAck,
        pendingAction,
        start,
        resendProof,
        submitProof,
        submitTotpSetup,
        finalizeRecoveryCodes,
        completeDone,
        close: resetLocal,
        isOpen: step === 'proof' || step === 'totp_setup' || step === 'recovery_codes' || step === 'done',
    }
}
